import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  SimpleGrid,
  Skeleton,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { ArrowRight, Bell, Check, Clock, Inbox, Plane, type LucideIcon } from "lucide-react";
import {
  fetchManagerDashboard,
  remindUnsubmitted,
  type DashboardEmployeeRow,
  type ManagerDashboard as ManagerDashboardData,
  type RemindResult,
} from "../api/client";
import { ACCENTS, NEUTRALS, STATUS } from "../theme/tokens";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return "Доброй ночи";
  if (h < 12) return "Доброе утро";
  if (h < 18) return "Добрый день";
  return "Добрый вечер";
}

// Статус недели → статус-слот палитры (для бейджа в строке).
const STATUS_SLOT: Record<string, keyof (typeof STATUS)["light"]> = {
  draft: "draft",
  no_week: "rejected",
  rework_requested_by_line_manager: "rework",
  rework_requested_by_project_manager: "rework",
  rolled_back_by_admin: "rejected",
};

// ЭПИК C: бейдж «почему человек в моём списке» (для ЛР+РП-гибрида).
const ROLE_BADGE: Record<string, string> = {
  lr: "подчинённый",
  pm: "проект",
};

export function ManagerDashboard() {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const scheme = dark ? "dark" : "light";
  const neutrals = theme.other?.neutrals?.[scheme] ?? NEUTRALS[scheme];
  const status = theme.other?.statuses?.[scheme] ?? STATUS[scheme];
  const accent = ACCENTS.indigo;

  const [data, setData] = useState<ManagerDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<RemindResult | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchManagerDashboard()
      .then((d) => {
        setData(d);
        // По умолчанию выбраны все, кому можно напомнить (can_remind).
        const ids = new Set<number>();
        for (const e of d.not_submitted_list) if (e.can_remind) ids.add(e.employee_id);
        setSelected(ids);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleRemind = useCallback(async () => {
    if (!data || selected.size === 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await remindUnsubmitted({
        employeeIds: [...selected],
        weekStart: data.chase_week_start,
      });
      setResult(res);
      load(); // обновить данные после отправки
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  }, [data, selected, load]);

  if (error) {
    return (
      <Alert color="red" title="Не удалось загрузить сводку" mb="md">
        {error}
        <Group mt="sm">
          <Button size="xs" onClick={load}>
            Повторить
          </Button>
        </Group>
      </Alert>
    );
  }

  if (!data) {
    return <DashboardSkeleton neutrals={neutrals} />;
  }

  const { kpi } = data;
  // ЭПИК C 1.1: у чистого РП карточки «Недосписанные часы» нет (норму гоняет
  // ЛР) — бэкенд отдаёт show_hours_debt=false; долг тогда не влияет и на
  // «всё закрыто». Отсутствие поля (старый бэкенд) = показывать, как раньше.
  const showHoursDebt = data.show_hours_debt !== false;
  const allClosed =
    kpi.pending_approvals === 0 &&
    kpi.not_submitted.count === 0 &&
    (!showHoursDebt || parseFloat(kpi.hours_debt) <= 0);
  const chaseList = data.not_submitted_list;
  // Роль-бейджи в строках показываем только гибриду (в списке есть и «lr», и
  // «pm» причины) — иначе бейдж не несёт информации.
  const showRoleBadges =
    chaseList.some((e) => (e.roles ?? []).includes("lr")) &&
    chaseList.some((e) => (e.roles ?? []).includes("pm"));

  const weekLabel = `${dayjs(data.chase_week_start).format("D")}–${dayjs(data.chase_week_end).format("D MMMM")}`;

  // ЭПИК C этап 1: карточка «Не сдали» убрана (дублировала блок ниже и могла
  // расходиться с ним числом) — её число теперь в заголовке блока. У каждой
  // оставшейся карточки подписано ОКНО ВРЕМЕНИ (раньше «Долг» молча считался
  // за прошлую неделю, а «Отсутствуют» — за две — и сверить было нельзя).
  const kpiCards: {
    slot: keyof typeof status;
    icon: LucideIcon;
    label: string;
    value: string;
    hint: string;
    to: string;
    calm: boolean;
  }[] = [
    {
      slot: "submitted",
      icon: Inbox,
      label: "Ждут согласования",
      value: String(kpi.pending_approvals),
      // ЭПИК C 1.1: число = бейджу «На согласовании» (таймшиты + отсутствия).
      hint:
        kpi.pending_approvals === 0 ? "сейчас · очередь пуста" : "сейчас · таймшиты и отсутствия",
      to: "/approvals",
      calm: allClosed,
    },
    ...(showHoursDebt
      ? [
          {
            slot: "rework" as const,
            icon: Clock,
            label: "Недосписанные часы",
            value: `${Math.round(parseFloat(kpi.hours_debt))} ч`,
            hint:
              parseFloat(kpi.hours_debt) <= 0
                ? `неделя ${weekLabel} · недосписаний нет`
                : `неделя ${weekLabel} · в согласованных неделях`,
            // Недосписались = согласованные с долгом → Исключения, отфильтровано.
            to: "/reports/exceptions?kind=underbooked",
            calm: allClosed,
          },
        ]
      : []),
    {
      slot: "absence",
      icon: Plane,
      label: "Отсутствуют сейчас",
      value: String(kpi.absent_now),
      // Число 1:1 равно блоку «Отсутствуют сейчас» на экране-цели; «+M» —
      // блоку «Предстоящие» (горизонт 30 дней, как на экране).
      hint:
        kpi.absent_upcoming_30d > 0
          ? `сегодня · ещё ${kpi.absent_upcoming_30d} в ближайшие 30 дней`
          : "сегодня · в ближайшие 30 дней никто не уходит",
      to: "/dashboard/absences",
      calm: false, // отсутствия не «проблема» — карточка всегда в цвете
    },
  ];

  return (
    <Box>
      {/* Шапка приветствия + CTA «Мой таймшит» */}
      <Group justify="space-between" align="flex-end" wrap="wrap" mb="lg" gap="sm">
        <div>
          <Text fz={24} fw={600} style={{ letterSpacing: "-0.02em", color: neutrals.text }}>
            {greeting()}
            {data.greeting_name ? `, ${data.greeting_name}` : ""}
          </Text>
          <Text fz={13} c="dimmed" mt={4}>
            {dayjs().format("dddd, D MMMM")}
          </Text>
        </div>
        <Button
          color="indigo"
          rightSection={<ArrowRight size={15} />}
          onClick={() => navigate("/timesheet")}
        >
          Мой таймшит
        </Button>
      </Group>

      {/* KPI-карточки (ЭПИК C: «Не сдали» слита с блоком ниже; у чистого РП
          «Недосписанные часы» скрыта — карточек может быть 2) */}
      <SimpleGrid cols={{ base: 1, sm: kpiCards.length }} spacing="md" mb="lg">
        {kpiCards.map((c) => {
          const tok = status[c.slot];
          const sqBg = c.calm ? neutrals.surfaceAlt : tok.bg;
          const sqFg = c.calm ? neutrals.textDim : tok.fg;
          const numColor = c.calm ? neutrals.text : tok.fg;
          const Icon = c.icon;
          return (
            <Card
              key={c.label}
              withBorder
              radius="lg"
              padding="md"
              onClick={() => navigate(c.to)}
              style={{ cursor: "pointer" }}
              data-testid={`kpi-${c.slot}`}
            >
              <Group gap={10} mb={12} wrap="nowrap">
                <Box
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: sqBg,
                    color: sqFg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={17} />
                </Box>
                <Text fz={12.5} fw={500} c="dimmed" lh={1.3}>
                  {c.label}
                </Text>
              </Group>
              <Text
                style={{
                  fontFamily: "var(--mantine-font-family-monospace)",
                  fontSize: 30,
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                  color: numColor,
                }}
              >
                {c.value}
              </Text>
              <Text fz={11.5} c="dimmed" mt={6} lh={1.35}>
                {c.hint}
              </Text>
            </Card>
          );
        })}
      </SimpleGrid>

      <div>
        {allClosed ? (
          <Card withBorder radius="lg" padding={48} style={{ textAlign: "center" }}>
            <Box
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                background: status.approved.bg,
                color: status.approved.fg,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <Check size={30} />
            </Box>
            <Text fz={18} fw={600} style={{ color: neutrals.text }}>
              Все закрыли период
            </Text>
            <Text fz={13} c="dimmed" mt={6} maw={420} mx="auto" lh={1.5}>
              Очередь согласований пуста, должников по часам нет. Можно заняться своим таймшитом.
            </Text>
          </Card>
        ) : (
          <Card withBorder radius="lg" padding={0} style={{ overflow: "hidden" }}>
            {/* Хедер chase-list */}
            <Group
              justify="space-between"
              align="center"
              wrap="wrap"
              gap="sm"
              p="md"
              style={{ borderBottom: `1px solid ${neutrals.border}` }}
            >
              <div>
                <Text
                  fz={14.5}
                  fw={600}
                  style={{ color: neutrals.text }}
                  data-testid="chase-header"
                >
                  Не сдали неделю {weekLabel} — {kpi.not_submitted.count} из{" "}
                  {kpi.not_submitted.total}
                </Text>
                <Text fz={11.5} c="dimmed" mt={2}>
                  прошлая закрытая неделя · число равно списку ниже
                </Text>
              </div>
              <Button
                variant="default"
                leftSection={<Bell size={15} />}
                disabled={selected.size === 0 || sending}
                loading={sending}
                onClick={() => void handleRemind()}
                data-testid="remind-btn"
              >
                Напомнить выбранным ({selected.size})
              </Button>
            </Group>

            <EmployeeRows
              employees={chaseList}
              showRoleBadges={showRoleBadges}
              selected={selected}
              onToggle={toggle}
              neutrals={neutrals}
              status={status}
              accent={accent}
            />

            {result && (
              <Alert
                color={result.sent > 0 ? "green" : "gray"}
                variant="light"
                m="md"
                data-testid="remind-result"
              >
                {result.sent > 0
                  ? `Отправлено напоминаний: ${result.sent}.`
                  : "Письма не отправлены."}
                {renderSkips(result)}
              </Alert>
            )}

            <Text fz={11.5} c="dimmed" px="md" pb="md" pt={result ? 0 : "xs"} maw={620} lh={1.5}>
              Напоминание уйдёт только тем, кто завершил онбординг (установил пароль). Одному
              сотруднику по одной неделе — не больше письма в сутки, считая плановую рассылку в
              понедельник утром. На следующий день напомнить можно снова.
            </Text>
          </Card>
        )}
      </div>
    </Box>
  );
}

function renderSkips(r: RemindResult): string {
  const parts: string[] = [];
  if (r.skipped_no_login) parts.push(`${r.skipped_no_login} не завершили онбординг`);
  if (r.skipped_throttled) parts.push(`${r.skipped_throttled} уже получили письмо сегодня`);
  if (r.skipped_optout) parts.push(`${r.skipped_optout} отключили письма`);
  if (r.skipped_out_of_scope) parts.push(`${r.skipped_out_of_scope} вне зоны видимости`);
  return parts.length ? ` Пропущено: ${parts.join(", ")}.` : "";
}

interface EmployeeRowsProps {
  employees: DashboardEmployeeRow[];
  /** Бейджи «подчинённый»/«проект» — только когда в списке есть обе причины. */
  showRoleBadges: boolean;
  selected: Set<number>;
  onToggle: (id: number) => void;
  neutrals: (typeof NEUTRALS)["light"];
  status: (typeof STATUS)["light"];
  accent: (typeof ACCENTS)["indigo"];
}

function EmployeeRows({
  employees,
  showRoleBadges,
  selected,
  onToggle,
  neutrals,
  status,
  accent,
}: EmployeeRowsProps) {
  return (
    <>
      {employees.map((emp) => {
        const slot = STATUS_SLOT[emp.timesheet_status] ?? "draft";
        const tok = status[slot];
        const row = (
          <Group
            key={emp.employee_id}
            gap={12}
            px="md"
            py={11}
            wrap="nowrap"
            style={{ borderTop: `1px solid ${neutrals.border}` }}
            data-testid={`chase-row-${emp.employee_id}`}
          >
            <Checkbox
              size="sm"
              color="indigo"
              checked={selected.has(emp.employee_id)}
              disabled={!emp.can_remind}
              onChange={() => onToggle(emp.employee_id)}
              aria-label={`Выбрать ${emp.full_name}`}
              data-testid={`remind-cb-${emp.employee_id}`}
            />
            <Avatar
              size={32}
              radius="xl"
              styles={{
                placeholder: {
                  background: accent[100],
                  color: accent[600],
                  fontSize: 12,
                  fontWeight: 600,
                },
              }}
            >
              {emp.initials}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <Text fz={13.5} fw={500} truncate style={{ color: neutrals.text }}>
                  {emp.full_name}
                </Text>
                {showRoleBadges &&
                  (emp.roles ?? []).map((r) => (
                    <Badge
                      key={r}
                      size="xs"
                      variant="light"
                      color={r === "lr" ? "indigo" : "teal"}
                      style={{ flexShrink: 0 }}
                    >
                      {ROLE_BADGE[r]}
                    </Badge>
                  ))}
              </Group>
            </div>
            <Box
              style={{
                background: tok.bg,
                color: tok.fg,
                fontSize: 11.5,
                fontWeight: 600,
                padding: "3px 9px",
                borderRadius: 999,
                whiteSpace: "nowrap",
              }}
            >
              {emp.timesheet_status_display}
            </Box>
          </Group>
        );
        if (emp.can_remind) return row;
        return (
          <Tooltip
            key={emp.employee_id}
            label="Не завершил онбординг — напоминание не отправится"
            position="top-start"
          >
            {row}
          </Tooltip>
        );
      })}
    </>
  );
}

function DashboardSkeleton({ neutrals }: { neutrals: (typeof NEUTRALS)["light"] }) {
  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <div>
          <Skeleton h={26} w={220} mb={8} />
          <Skeleton h={14} w={160} />
        </div>
        <Skeleton h={38} w={140} radius="md" />
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
        {[0, 1, 2].map((i) => (
          <Card key={i} withBorder radius="lg" padding="md">
            <Group gap={10} mb={12}>
              <Skeleton h={34} w={34} radius="md" />
              <Skeleton h={14} w={90} />
            </Group>
            <Skeleton h={28} w={64} mb={8} />
            <Skeleton h={12} w={110} />
          </Card>
        ))}
      </SimpleGrid>
      <Card withBorder radius="lg" padding="md">
        <Skeleton h={18} w={200} mb={16} />
        {[0, 1, 2].map((i) => (
          <Group key={i} gap={12} py={8} style={{ borderTop: `1px solid ${neutrals.border}` }}>
            <Skeleton h={17} w={17} radius={4} />
            <Skeleton h={32} w={32} circle />
            <Skeleton h={14} w={180} />
            <Box style={{ flex: 1 }} />
            <Skeleton h={20} w={70} radius="xl" />
          </Group>
        ))}
      </Card>
      <Group justify="center" mt="md">
        <Loader size="xs" />
      </Group>
    </Box>
  );
}
